import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import AdminUserCreate from "./AdminUserCreate";
import AdminUserDetail from "./AdminUserDetail";
import AdminUserDeleteDialog from "./dialog/AdminUserDeleteDialog";
import {
  getAdminUserList,
  getCompanyList,
  postAdminUserUpdate,
} from "../services/callApi";
import {
  getApiStatus,
  getManagementApiErrorMessage,
} from "../utils/managementApiError";
import { API_QUERY_KEYS, invalidateApiQueries } from "../utils/apiQueryCache";
import { buildAdminUserUpdatePayload } from "../utils/adminUserUpdatePayload";
import {
  MANAGEMENT_ACTION_CELL_SX,
  MANAGEMENT_BODY_CELL_SX,
  MANAGEMENT_HEADER_CELL_SX,
  MANAGEMENT_ROWS_PER_PAGE,
  MANAGEMENT_SPACER_BODY_CELL_SX,
  MANAGEMENT_SPACER_HEADER_CELL_SX,
  MANAGEMENT_TABLE_SX,
  ManagementEmptyState,
  ManagementIconActionButton,
  ManagementListPage,
  ManagementListPagination,
  ManagementTableContainer,
  ManagementTruncatedText,
} from "./common/ManagementListLayout";

function AdminUserList({ role, onForbidden }) {
  const [adminUsers, setAdminUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [selectedAdminUser, setSelectedAdminUser] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [page, setPage] = useState(0);

  const handleApiError = useCallback(
    (error) => {
      const status = getApiStatus(error);
      if (status === 401) {
        window.location.href = "/login";
        return;
      }
      if (status === 403) {
        onForbidden("adminUserList");
        return;
      }
      setErrorMessage(getManagementApiErrorMessage(error));
    },
    [onForbidden]
  );

  const loadData = useCallback(
    async (selectedId, showLoading = false) => {
      if (showLoading) {
        setLoading(true);
      }

      try {
        const [adminUserResponse, companyResponse] = await Promise.all([
          getAdminUserList(),
          getCompanyList(),
        ]);
        const nextAdminUsers = Array.isArray(adminUserResponse)
          ? [...adminUserResponse].sort((a, b) => b.adminUserId - a.adminUserId)
          : [];
        // 登録・編集画面では会社IDと会社名だけを扱い、受験用パスワードを保持しない。
        const nextCompanies = Array.isArray(companyResponse)
          ? companyResponse.map((company) => ({
              companyId: company.companyId,
              companyName: company.companyName,
            }))
          : [];

        setAdminUsers(nextAdminUsers);
        setCompanies(nextCompanies);
        if (selectedId != null) {
          setSelectedAdminUser(
            nextAdminUsers.find((adminUser) => adminUser.adminUserId === selectedId) ?? null
          );
        }
        setErrorMessage("");
        return nextAdminUsers;
      } catch (error) {
        handleApiError(error);
        return null;
      } finally {
        if (showLoading) {
          setLoading(false);
        }
      }
    },
    [handleApiError]
  );

  useEffect(() => {
    loadData(null, true);
  }, [loadData]);

  const companyNameById = useMemo(
    () => new Map(companies.map((company) => [company.companyId, company.companyName])),
    [companies]
  );
  const paginatedAdminUsers = adminUsers.slice(
    page * MANAGEMENT_ROWS_PER_PAGE,
    page * MANAGEMENT_ROWS_PER_PAGE + MANAGEMENT_ROWS_PER_PAGE
  );

  useEffect(() => {
    const lastPage = Math.max(0, Math.ceil(adminUsers.length / MANAGEMENT_ROWS_PER_PAGE) - 1);
    setPage((currentPage) => Math.min(currentPage, lastPage));
  }, [adminUsers.length]);

  const handleAdminUserUpdated = async (adminUserId) => {
    invalidateApiQueries(API_QUERY_KEYS.adminUserList);
    await loadData(adminUserId);
  };

  const handleAdminUserCreated = async () => {
    invalidateApiQueries(API_QUERY_KEYS.adminUserList);
    setIsCreateOpen(false);
    setSuccessMessage("管理者を登録しました。招待メールを送信しました。");
    await loadData();
  };

  const handleDeactivate = async (adminUser) => {
    setErrorMessage("");
    setSuccessMessage("");

    try {
      await postAdminUserUpdate(
        buildAdminUserUpdatePayload(adminUser, {
          adminUserName: adminUser.adminUserName,
          isActive: false,
          companyId: adminUser.companyId ?? "",
          role: adminUser.role,
        })
      );
      invalidateApiQueries(API_QUERY_KEYS.adminUserList);
      await loadData();
      setSuccessMessage("管理者を削除しました。");
      return true;
    } catch (error) {
      handleApiError(error);
      return false;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" sx={{ minHeight: 240 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isDetailOpen && selectedAdminUser) {
    return (
      <AdminUserDetail
        adminUser={selectedAdminUser}
        companies={companies}
        onBack={() => setIsDetailOpen(false)}
        onUpdated={handleAdminUserUpdated}
        onForbidden={onForbidden}
      />
    );
  }

  if (isCreateOpen && role === "super_admin") {
    return (
      <AdminUserCreate
        companies={companies}
        onBack={() => setIsCreateOpen(false)}
        onCreated={handleAdminUserCreated}
        onForbidden={onForbidden}
      />
    );
  }

  return (
    <ManagementListPage
      title="管理者一覧"
      count={adminUsers.length}
      action={
        role === "super_admin" && (
          <Button
            variant="contained"
            startIcon={<PersonAddIcon />}
            onClick={() => {
              setErrorMessage("");
              setSuccessMessage("");
              setIsCreateOpen(true);
            }}
          >
            新規登録
          </Button>
        )
      }
    >
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}
      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      )}
      {adminUsers.length === 0 && !errorMessage ? (
        <ManagementEmptyState>管理者は登録されていません。</ManagementEmptyState>
      ) : (
        <ManagementTableContainer>
          <Table sx={{ ...MANAGEMENT_TABLE_SX, minWidth: 1270 }} aria-label="管理者一覧">
          <colgroup>
            <col style={{ width: 72 }} />
            <col style={{ width: 220 }} />
            <col style={{ width: 160 }} />
            <col style={{ width: 140 }} />
            <col style={{ width: 220 }} />
            <col style={{ width: 220 }} />
            <col style={{ width: 88 }} />
            <col />
            <col style={{ width: 72 }} />
            <col style={{ width: 72 }} />
          </colgroup>
          <TableHead>
            <TableRow>
              <TableCell align="right" sx={MANAGEMENT_HEADER_CELL_SX}>ID</TableCell>
              <TableCell sx={MANAGEMENT_HEADER_CELL_SX}>ログインID</TableCell>
              <TableCell sx={MANAGEMENT_HEADER_CELL_SX}>氏名</TableCell>
              <TableCell sx={MANAGEMENT_HEADER_CELL_SX}>ロール</TableCell>
              <TableCell sx={MANAGEMENT_HEADER_CELL_SX}>会社</TableCell>
              <TableCell sx={MANAGEMENT_HEADER_CELL_SX}>作成日時</TableCell>
              <TableCell align="center" sx={MANAGEMENT_HEADER_CELL_SX}>状態</TableCell>
              <TableCell aria-hidden="true" sx={MANAGEMENT_SPACER_HEADER_CELL_SX} />
              <TableCell align="center" sx={MANAGEMENT_HEADER_CELL_SX}>編集</TableCell>
              <TableCell align="center" sx={MANAGEMENT_HEADER_CELL_SX}>削除</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedAdminUsers.map((adminUser) => (
              <TableRow key={adminUser.adminUserId} hover sx={{ "&:last-child td": { borderBottom: 0 } }}>
                <TableCell align="right" sx={MANAGEMENT_BODY_CELL_SX}>{adminUser.adminUserId}</TableCell>
                <TableCell sx={MANAGEMENT_BODY_CELL_SX}>
                  <ManagementTruncatedText>{adminUser.cognitoUsername}</ManagementTruncatedText>
                </TableCell>
                <TableCell sx={MANAGEMENT_BODY_CELL_SX}>
                  <ManagementTruncatedText>{adminUser.adminUserName}</ManagementTruncatedText>
                </TableCell>
                <TableCell sx={MANAGEMENT_BODY_CELL_SX}>{adminUser.role}</TableCell>
                <TableCell sx={MANAGEMENT_BODY_CELL_SX}>
                  <ManagementTruncatedText>
                    {adminUser.companyId == null
                      ? "－"
                      : companyNameById.get(adminUser.companyId) ?? adminUser.companyId}
                  </ManagementTruncatedText>
                </TableCell>
                <TableCell sx={{ ...MANAGEMENT_BODY_CELL_SX, whiteSpace: "nowrap" }}>
                  {adminUser.createdAt ?? "－"}
                </TableCell>
                <TableCell align="center" sx={MANAGEMENT_BODY_CELL_SX}>
                  <Chip
                    label={adminUser.isActive === false ? "無効" : "有効"}
                    color={adminUser.isActive === false ? "default" : "success"}
                    size="small"
                  />
                </TableCell>
                <TableCell aria-hidden="true" sx={MANAGEMENT_SPACER_BODY_CELL_SX} />
                <TableCell align="center" sx={MANAGEMENT_ACTION_CELL_SX}>
                  <ManagementIconActionButton
                    aria-label={`${adminUser.adminUserName}を編集`}
                    tooltip="編集"
                    onClick={() => {
                      setSelectedAdminUser(adminUser);
                      setIsDetailOpen(true);
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </ManagementIconActionButton>
                </TableCell>
                <TableCell align="center" sx={MANAGEMENT_ACTION_CELL_SX}>
                  <AdminUserDeleteDialog
                    adminUser={adminUser}
                    onDelete={handleDeactivate}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <ManagementListPagination
          count={adminUsers.length}
          page={page}
          onPageChange={(_, nextPage) => setPage(nextPage)}
        />
        </ManagementTableContainer>
      )}
    </ManagementListPage>
  );
}

export default AdminUserList;
